import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Alert,
  Snackbar,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Autocomplete,
  CircularProgress,
  Divider,
  Grid,
} from '@mui/material';
import { useTheme } from '@emotion/react';
import { tokens } from '../../theme';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { useNavigate, useParams } from 'react-router-dom';
import Headeer from '../../components/Headeer';
import FormulaService from '../../services/doctorServices/FormulaService';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import FunctionsIcon from '@mui/icons-material/Functions';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

// Debounce hook
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

const EditFormula = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { t } = useTranslation('formulas');
  const navigate = useNavigate();
  const { id } = useParams();
  const formulaService = new FormulaService();

  const [testResult, setTestResult] = useState(null);
  const [testLoading, setTestLoading] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [loading, setLoading] = useState(true);
  const [formula, setFormula] = useState(null);
  const [error, setError] = useState(null);

  // Validation Schema
  const getValidationSchema = () => Yup.object().shape({
    name: Yup.string()
      .required(t('formulaNameRequiredValidation'))
      .min(3, t('formulaNameMinLength'))
      .max(100, t('formulaNameMaxLength')),
    description: Yup.string()
      .max(500, t('descriptionMaxLength')),
    formula_expression: Yup.string()
      .required(t('formulaExpressionRequiredValidation'))
      .min(3, t('formulaExpressionMinLength')),
  });

  // Load formula
  useEffect(() => {
    fetchFormula();
  }, [id]);

  const fetchFormula = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await formulaService.getFormula(id);
      setFormula(data);
      
      // Initialize tracking state with loaded values
      setCurrentFormula(data.formula_expression || '');
      setCurrentCategories(data.required_categories || []);
      setCurrentKeys(data.required_keys || []);
      
      // Test initial formula
      testFormula(data.formula_expression, data.required_categories || [], data.required_keys || []);
    } catch (err) {
      console.error('Error loading formula:', err);
      // 403 (Permission) errors are handled by global interceptor
      if (err.response?.status === 403) {
        setTimeout(() => navigate('/formulas'), 2000);
      } else {
        setError(t('errorLoadingFormula'));
      }
    } finally {
      setLoading(false);
    }
  };

  // Test formula with debounce
  const testFormula = async (expression, categories, keys) => {
    if (!expression || expression.trim() === '') {
      setTestResult(null);
      return;
    }

    // Frontend validation first
    const validation = formulaService.validateFormulaExpression(expression);
    if (!validation.valid) {
      setTestResult({
        valid: false,
        error: validation.error,
      });
      return;
    }

    setTestLoading(true);
    try {
      const result = await formulaService.testFormula(
        expression,
        categories || [],
        keys || []
      );
      setTestResult(result);
    } catch (error) {
      setTestResult({
        valid: false,
        error: error.response?.data?.error || t('errorDuringTest'),
      });
    } finally {
      setTestLoading(false);
    }
  };

  // Variable ekleme
  const handleInsertVariable = (variable, currentValue, setFieldValue) => {
    const newValue = currentValue ? `${currentValue} ${variable}` : variable;
    setFieldValue('formula_expression', newValue);
    setCurrentFormula(newValue); // Update tracking state
  };

  // Form submit
  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const formulaData = {
        ...values,
        is_public: false,
      };
      
      await formulaService.updateFormula(id, formulaData);
      
      setSnackbarMessage(t('formulaUpdatedSuccessfully'));
      setSnackbarSeverity('success');
      setOpenSnackbar(true);
      
      // Redirect to list page after 2 seconds
      setTimeout(() => {
        navigate('/formulas');
      }, 2000);
      
    } catch (error) {
      // 403 (Permission) errors are handled by global interceptor
      if (error.response?.status !== 403) {
        const errorMsg = error.response?.data?.error || t('errorUpdatingFormula');
        setSnackbarMessage(`❌ ${errorMsg}`);
        setSnackbarSeverity('error');
        setOpenSnackbar(true);
      }
      // For 403 errors, global alert will be shown automatically
    } finally {
      setSubmitting(false);
    }
  };

  const variables = formulaService.getAvailableVariables();

  // State for current form values (to track outside Formik)
  const [currentFormula, setCurrentFormula] = useState('');
  const [currentCategories, setCurrentCategories] = useState([]);
  const [currentKeys, setCurrentKeys] = useState([]);

  // Debounced formula - MUST be at component level
  const debouncedFormula = useDebounce(currentFormula, 800);

  // Test formula when debounced value changes - MUST be at component level
  useEffect(() => {
    if (debouncedFormula) {
      testFormula(debouncedFormula, currentCategories, currentKeys);
    }
  }, [debouncedFormula, currentCategories, currentKeys]);

  if (loading) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="calc(100vh - 100px)"
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error || !formula) {
    return (
      <Box m="20px">
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || t('formulaNotFound')}
        </Alert>
        <Button 
          variant="contained" 
          onClick={() => navigate('/formulas')}
          startIcon={<ArrowBackIcon />}
        >
          {t('backToFormulas')}
        </Button>
      </Box>
    );
  }

  return (
    <Box m="20px">
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/formulas')}
            sx={{
              color: colors.grey[100],
              mb: 2,
              '&:hover': {
                backgroundColor: colors.primary[400],
              },
            }}
          >
            {t('back')}
          </Button>
          <Headeer
            title={t('editScoringFormula')}
            subtitle={t('editScoringFormulaSubtitle')}
          />
        </Box>
      </Box>

      <Formik
        initialValues={{
          name: formula.name || '',
          description: formula.description || '',
          formula_expression: formula.formula_expression || '',
          required_categories: formula.required_categories || [],
          required_keys: formula.required_keys || [],
        }}
        validationSchema={getValidationSchema()}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          setFieldValue,
          isSubmitting,
        }) => {
          // Custom change handler that also updates tracking state
          const handleFormulaChange = (e) => {
            handleChange(e);
            setCurrentFormula(e.target.value);
          };

          const handleCategoriesChange = (e, newValue) => {
            setFieldValue('required_categories', newValue);
            setCurrentCategories(newValue);
          };

          const handleKeysChange = (e, newValue) => {
            setFieldValue('required_keys', newValue);
            setCurrentKeys(newValue);
          };

          return (
            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                {/* Left Column - Form */}
                <Grid item xs={12} md={8}>
                  <Paper
                    sx={{
                      p: 3,
                      backgroundColor: colors.primary[400],
                      borderRadius: '12px',
                      boxShadow: `0 4px 24px rgba(0, 0, 0, 0.15)`,
                    }}
                  >
                    {/* Formula Name */}
                    <TextField
                      fullWidth
                      label={t('formulaNameRequired')}
                      name="name"
                      value={values.name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.name && Boolean(errors.name)}
                      helperText={touched.name && errors.name}
                      placeholder={t('formulaNamePlaceholder')}
                      sx={{ mb: 3 }}
                    />

                    {/* Description */}
                    <TextField
                      fullWidth
                      label={t('descriptionLabel')}
                      name="description"
                      value={values.description}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.description && Boolean(errors.description)}
                      helperText={touched.description && errors.description}
                      placeholder={t('descriptionPlaceholder')}
                      multiline
                      rows={2}
                      sx={{ mb: 3 }}
                    />

                    {/* Formula Expression */}
                    <Box mb={3}>
                      <TextField
                        fullWidth
                        label={t('formulaExpressionRequired')}
                        name="formula_expression"
                        value={values.formula_expression}
                        onChange={handleFormulaChange}
                        onBlur={handleBlur}
                        error={touched.formula_expression && Boolean(errors.formula_expression)}
                        helperText={
                          touched.formula_expression && errors.formula_expression
                            ? errors.formula_expression
                            : t('formulaExpressionHelper')
                        }
                        placeholder={t('formulaExpressionPlaceholder')}
                        multiline
                        rows={4}
                        sx={{
                          '& textarea': {
                            fontFamily: 'monospace',
                            fontSize: '0.95rem',
                          },
                        }}
                      />

                      {/* Test Result */}
                      {testLoading && (
                        <Box mt={2} display="flex" alignItems="center" gap={1}>
                          <CircularProgress size={20} />
                          <Typography variant="body2" color={colors.grey[400]}>
                            {t('testing')}
                          </Typography>
                        </Box>
                      )}

                      {testResult && !testLoading && (
                        <Alert
                          severity={testResult.valid ? 'success' : 'error'}
                          icon={testResult.valid ? <CheckCircleIcon /> : <ErrorIcon />}
                          sx={{ mt: 2 }}
                        >
                          {testResult.valid ? (
                            <Box>
                              <Typography variant="body2" fontWeight={600}>
                                {t('formulaIsValid')}
                              </Typography>
                              <Typography variant="caption" display="block" mt={1}>
                                {t('testScore')} <strong>{testResult.testScore}</strong>
                              </Typography>
                              <Typography
                                variant="caption"
                                display="block"
                                sx={{ fontFamily: 'monospace', mt: 0.5 }}
                              >
                                {t('calculated')} {testResult.testExpression}
                              </Typography>
                            </Box>
                          ) : (
                            <Box>
                              <Typography variant="body2" fontWeight={600}>
                                {t('formulaIsInvalid')}
                              </Typography>
                              <Typography variant="caption" display="block" mt={1}>
                                {testResult.error}
                              </Typography>
                            </Box>
                          )}
                        </Alert>
                      )}
                    </Box>

                    {/* Required Categories */}
                    <Autocomplete
                      multiple
                      freeSolo
                      options={[]}
                      value={values.required_categories || []}
                      onChange={handleCategoriesChange}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={t('requiredCategoriesLabel')}
                          placeholder={t('addCategoryPlaceholder')}
                          helperText={t('addCategoryHelper')}
                        />
                      )}
                      renderTags={(value, getTagProps) =>
                        (value || []).map((option, index) => (
                          <Chip
                            label={option}
                            {...getTagProps({ index })}
                            sx={{
                              backgroundColor: `${colors.greenAccent[600]}30`,
                              color: colors.greenAccent[300],
                            }}
                          />
                        ))
                      }
                      sx={{ mb: 3 }}
                    />

                    {/* Required Keys */}
                    <Autocomplete
                      multiple
                      freeSolo
                      options={[]}
                      value={values.required_keys || []}
                      onChange={handleKeysChange}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={t('requiredQuestionKeys')}
                          placeholder={t('addKeyPlaceholder')}
                          helperText={t('addKeyHelper')}
                        />
                      )}
                      renderTags={(value, getTagProps) =>
                        (value || []).map((option, index) => (
                          <Chip
                            label={option}
                            {...getTagProps({ index })}
                            sx={{
                              backgroundColor: `${colors.blueAccent[600]}30`,
                              color: colors.blueAccent[300],
                            }}
                          />
                        ))
                      }
                      sx={{ mb: 3 }}
                    />

                    {/* Action Buttons */}
                    <Box display="flex" gap={2} mt={4}>
                      <Button
                        type="submit"
                        variant="contained"
                        disabled={isSubmitting || !testResult?.valid}
                        startIcon={isSubmitting ? <CircularProgress size={20} /> : <SaveIcon />}
                        sx={{
                          backgroundColor: colors.greenAccent[600],
                          color: colors.grey[100],
                          fontSize: '14px',
                          fontWeight: 'bold',
                          padding: '12px 32px',
                          borderRadius: '8px',
                          '&:hover': {
                            backgroundColor: colors.greenAccent[700],
                          },
                          '&:disabled': {
                            backgroundColor: colors.grey[700],
                          },
                        }}
                      >
                        {isSubmitting ? t('updating') : t('updateFormula')}
                      </Button>

                      <Button
                        variant="outlined"
                        onClick={() => navigate('/formulas')}
                        startIcon={<CancelIcon />}
                        sx={{
                          color: colors.grey[300],
                          borderColor: colors.grey[600],
                          '&:hover': {
                            borderColor: colors.grey[400],
                          },
                        }}
                      >
                        {t('cancel')}
                      </Button>
                    </Box>
                  </Paper>
                </Grid>

                {/* Right Column - Helper */}
                <Grid item xs={12} md={4}>
                  {/* Available Variables */}
                  <Paper
                    sx={{
                      p: 2,
                      mb: 2,
                      backgroundColor: colors.primary[400],
                      borderRadius: '12px',
                      maxHeight: '85vh',
                      overflowY: 'auto',
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        mb: 2,
                        color: colors.grey[100],
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      <FunctionsIcon sx={{ color: colors.blueAccent[400] }} />
                      {t('availableVariables')}
                    </Typography>

                    {/* Global Variables */}
                    <Accordion
                      defaultExpanded
                      sx={{
                        backgroundColor: colors.primary[500],
                        mb: 1,
                      }}
                    >
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography fontWeight={600}>{t('globalVariables')}</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        {variables.global.map((v, index) => (
                          <Box key={index} mb={1}>
                            <Chip
                              label={v.var}
                              size="small"
                              onClick={() =>
                                handleInsertVariable(
                                  v.example,
                                  values.formula_expression,
                                  setFieldValue
                                )
                              }
                              icon={<ContentCopyIcon fontSize="small" />}
                              sx={{
                                backgroundColor: `${colors.blueAccent[600]}30`,
                                color: colors.blueAccent[300],
                                cursor: 'pointer',
                                '&:hover': {
                                  backgroundColor: `${colors.blueAccent[600]}50`,
                                },
                              }}
                            />
                            <Typography variant="caption" display="block" ml={1} mt={0.5}>
                              {v.desc}
                            </Typography>
                          </Box>
                        ))}
                      </AccordionDetails>
                    </Accordion>

                    {/* Question Variables */}
                    <Accordion sx={{ backgroundColor: colors.primary[500], mb: 1 }}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography fontWeight={600}>{t('questionVariables')}</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        {variables.questions.map((v, index) => (
                          <Box key={index} mb={1}>
                            <Chip
                              label={v.var}
                              size="small"
                              onClick={() =>
                                handleInsertVariable(
                                  v.example,
                                  values.formula_expression,
                                  setFieldValue
                                )
                              }
                              icon={<ContentCopyIcon fontSize="small" />}
                              sx={{
                                backgroundColor: `${colors.greenAccent[600]}30`,
                                color: colors.greenAccent[300],
                                cursor: 'pointer',
                                '&:hover': {
                                  backgroundColor: `${colors.greenAccent[600]}50`,
                                },
                              }}
                            />
                            <Typography variant="caption" display="block" ml={1} mt={0.5}>
                              {v.desc}
                            </Typography>
                          </Box>
                        ))}
                      </AccordionDetails>
                    </Accordion>

                    {/* Category Variables */}
                    <Accordion sx={{ backgroundColor: colors.primary[500], mb: 1 }}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography fontWeight={600}>{t('categoryVariables')}</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        {variables.categories.map((v, index) => (
                          <Box key={index} mb={1}>
                            <Chip
                              label={v.var}
                              size="small"
                              onClick={() =>
                                handleInsertVariable(
                                  v.example,
                                  values.formula_expression,
                                  setFieldValue
                                )
                              }
                              icon={<ContentCopyIcon fontSize="small" />}
                              sx={{
                                backgroundColor: `${colors.redAccent[600]}30`,
                                color: colors.redAccent[300],
                                cursor: 'pointer',
                                '&:hover': {
                                  backgroundColor: `${colors.redAccent[600]}50`,
                                },
                              }}
                            />
                            <Typography variant="caption" display="block" ml={1} mt={0.5}>
                              {v.desc}
                            </Typography>
                          </Box>
                        ))}
                      </AccordionDetails>
                    </Accordion>

                    {/* Operators */}
                    <Accordion sx={{ backgroundColor: colors.primary[500] }}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography fontWeight={600}>{t('operators')}</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        {variables.operators.map((v, index) => (
                          <Box key={index} mb={1}>
                            <Chip
                              label={v.var}
                              size="small"
                              sx={{
                                backgroundColor: `${colors.grey[600]}30`,
                                color: colors.grey[300],
                              }}
                            />
                            <Typography variant="caption" display="block" ml={1} mt={0.5}>
                              {v.desc}
                            </Typography>
                          </Box>
                        ))}
                      </AccordionDetails>
                    </Accordion>
                  </Paper>
                </Grid>
              </Grid>
            </form>
          );
        }}
      </Formik>

      {/* Snackbar */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setOpenSnackbar(false)}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EditFormula;

